import React from 'react'
import { router, usePage } from '@inertiajs/react'
import {
  Box, Button, Paper, Typography, Chip, Alert,
  Table, TableBody, TableCell, TableRow,
} from '@mui/material'
import GavelIcon from '@mui/icons-material/Gavel'
import AppShell from '../../../../components/layout/AppShell'
import PageHeader from '../../../../components/ui/PageHeader'
import StatusBadge from '../../../../components/ui/StatusBadge'
import FormattedCurrency from '../../../../components/ui/FormattedCurrency'

Show.layout = (page) => <AppShell>{page}</AppShell>

const MAJORITY_LABELS = {
  simple_majority: 'Einfache Mehrheit', qualified_majority: 'Qualifizierte Mehrheit',
  unanimity: 'Einstimmigkeit', double_qualified: 'Doppelt qualifizierte Mehrheit'
}
const VOTING_LABELS = {
  head_count: 'Kopfprinzip', value_based: 'Wertprinzip', unit_based: 'Einheitsprinzip'
}

export default function Show({ property, resolution, special_levy }) {
  const { props } = usePage()
  const tenant = props.tenant || {}

  const canFinalize = resolution.status === 'accepted' && resolution.challengeable === false

  const handleFinalize = () => {
    router.patch(`/m/${tenant.slug}/properties/${property.id}/resolutions/${resolution.id}/finalize`)
  }

  return (
    <Box>
      <PageHeader
        title={`Beschluss #${resolution.resolution_number}`}
        breadcrumbs={[
          { label: 'Beschlusssammlung', href: `/m/${tenant.slug}/properties/${property.id}/resolutions` },
          { label: `#${resolution.resolution_number}` },
        ]}
        actions={
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canFinalize && (
              <Button variant="outlined" size="small" onClick={handleFinalize} data-testid="finalize-btn">
                Bestandskräftig setzen
              </Button>
            )}
            {resolution.status === 'accepted' && !special_levy && (
              <Button variant="outlined" size="small" startIcon={<GavelIcon />}
                onClick={() => router.visit(
                  `/m/${tenant.slug}/properties/${property.id}/special_levies/new?resolution_id=${resolution.id}`
                )}
                data-testid="create-levy-btn">
                Sonderumlage anlegen
              </Button>
            )}
          </Box>
        }
      />

      {/* Header info */}
      <Paper elevation={0} sx={{ p: 2, mb: 2, border: '1px solid', borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <StatusBadge type="resolution_status" value={resolution.status} />
          <Typography variant="body2" color="text.secondary">Datum: {resolution.date}</Typography>
          {resolution.assembly_date && (
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">Versammlung: {resolution.assembly_date}</Typography>
              <StatusBadge type="assembly_type" value={resolution.assembly_type} size="small" />
            </Box>
          )}
          {resolution.agenda_item_title && (
            <Typography variant="body2" color="text.secondary">
              TOP {resolution.agenda_item_position}: {resolution.agenda_item_title}
            </Typography>
          )}
        </Box>
      </Paper>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
        {/* Resolution text */}
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Beschlusstext</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{resolution.resolution_text}</Typography>
        </Paper>

        {/* Voting */}
        <Paper elevation={0} sx={{ p: 2, border: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle2" fontWeight={600} gutterBottom>Abstimmung</Typography>
          <Table size="small">
            <TableBody>
              {[
                ['Abstimmungsart', VOTING_LABELS[resolution.voting_method] || resolution.voting_method],
                ['Mehrheitserfordernis', MAJORITY_LABELS[resolution.majority_requirement] || resolution.majority_requirement],
                ['Ja-Stimmen', resolution.votes_for],
                ['Nein-Stimmen', resolution.votes_against],
                ['Enthaltungen', resolution.abstentions],
                ['Anfechtungsfrist', resolution.challenge_deadline || '–'],
              ].map(([label, value]) => value != null && (
                <TableRow key={label}>
                  <TableCell sx={{ borderBottom: 'none', py: 0.5, color: 'text.secondary', fontSize: '0.8rem' }}>{label}</TableCell>
                  <TableCell sx={{ borderBottom: 'none', py: 0.5, fontSize: '0.85rem' }}>{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </Box>

      {/* Sonderumlage link */}
      {special_levy && (
        <Paper elevation={0} sx={{ p: 2, mt: 2, border: '1px solid', borderColor: 'divider', cursor: 'pointer' }}
          onClick={() => router.visit(`/m/${tenant.slug}/properties/${property.id}/special_levies/${special_levy.id}`)}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <GavelIcon color="primary" />
            <Box>
              <Typography variant="body2" fontWeight={500}>Sonderumlage: {special_levy.name}</Typography>
              <Typography variant="caption" color="text.secondary">
                {special_levy.amount ? <FormattedCurrency value={special_levy.amount} align="left" /> : ''} ·
                <StatusBadge type="special_levy_status" value={special_levy.status} size="small" />
              </Typography>
            </Box>
          </Box>
        </Paper>
      )}
    </Box>
  )
}
